import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Plus, FolderPlus } from "@phosphor-icons/react";
import { faqApi, FaqApiError } from "@/components/admin/faq/api";
import { filterFaqItems } from "@/components/admin/faq/utils";
import { FaqCategoryTabs } from "@/components/admin/faq/FaqCategoryTabs";
import { FaqItemRow } from "@/components/admin/faq/FaqItemRow";
import { FaqEmptyState } from "@/components/admin/faq/FaqEmptyState";
import { FaqItemListSkeleton } from "@/components/admin/faq/FaqItemListSkeleton";
import { FaqCategoryFormDialog } from "@/components/admin/faq/FaqCategoryFormDialog";
import { FaqItemFormDialog } from "@/components/admin/faq/FaqItemFormDialog";
import { DEFAULT_FAQ_ICON } from "@/lib/faq-icons";
import type { FaqCategory, FaqItem } from "@/types/faq";

function invalidateFaqQueries(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["admin", "faq"] });
  qc.invalidateQueries({ queryKey: ["faq", "public"] });
}

export function FaqSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [activeCategoryId, setActiveCategoryId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryForm, setCategoryForm] = useState<Partial<FaqCategory> | null>(null);
  const [itemForm, setItemForm] = useState<Partial<FaqItem> | null>(null);

  const { data: catData, isLoading: catsLoading } = useQuery({
    queryKey: ["admin", "faq", "categories"],
    queryFn: () =>
      faqApi<{ categories: FaqCategory[] }>(
        "/api/faq/categories",
        undefined,
        t("faqAdmin.fetchCategoriesError"),
      ),
  });
  const categories = catData?.categories ?? [];

  useEffect(() => {
    if (categories.length === 0) {
      setActiveCategoryId("");
      return;
    }
    if (!activeCategoryId || !categories.some((c) => c.id === activeCategoryId)) {
      setActiveCategoryId(categories[0]!.id);
    }
  }, [categories, activeCategoryId]);

  const activeCategory = categories.find((c) => c.id === activeCategoryId);

  const { data: itemsData, isLoading: itemsLoading } = useQuery({
    queryKey: ["admin", "faq", "items", activeCategoryId],
    queryFn: () =>
      faqApi<{ items: FaqItem[] }>(
        `/api/faq/items?categoryId=${activeCategoryId}`,
        undefined,
        t("faqAdmin.fetchItemsError"),
      ),
    enabled: Boolean(activeCategoryId),
  });

  const items = itemsData?.items ?? [];
  const filteredItems = useMemo(() => filterFaqItems(items, searchQuery), [items, searchQuery]);

  const saveCat = useMutation({
    mutationFn: async (c: Partial<FaqCategory>) => {
      const isEdit = Boolean(c.id);
      return faqApi(
        isEdit ? `/api/faq/categories/${c.id}` : "/api/faq/categories",
        {
          method: isEdit ? "PATCH" : "POST",
          body: JSON.stringify({
            nameBs: c.nameBs,
            nameEn: c.nameEn,
            icon: c.icon,
            placements: c.placements ?? [],
            isActive: c.isActive ?? true,
          }),
        },
        t("faqAdmin.saveCategoryError"),
      );
    },
    onSuccess: () => {
      invalidateFaqQueries(qc);
      setCategoryForm(null);
      toast.success(t("faqAdmin.save"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteCat = useMutation({
    mutationFn: (id: string) =>
      faqApi(`/api/faq/categories/${id}`, { method: "DELETE" }, t("faqAdmin.deleteCategoryError")),
    onSuccess: () => {
      invalidateFaqQueries(qc);
      setCategoryForm(null);
      toast.success(t("faqAdmin.deleted"));
    },
    onError: (e: Error) => {
      if (e instanceof FaqApiError && e.code === "has_items") {
        toast.error(t("faqAdmin.deleteHasItems"));
      } else {
        toast.error(e.message);
      }
    },
  });

  const saveItem = useMutation({
    mutationFn: async (item: Partial<FaqItem>) => {
      const isEdit = Boolean(item.id);
      return faqApi(
        isEdit ? `/api/faq/items/${item.id}` : "/api/faq/items",
        {
          method: isEdit ? "PATCH" : "POST",
          body: JSON.stringify({
            categoryId: activeCategoryId,
            questionBs: item.questionBs,
            questionEn: item.questionEn,
            answerBs: item.answerBs,
            answerEn: item.answerEn,
            isActive: item.isActive ?? true,
          }),
        },
        t("faqAdmin.saveItemError"),
      );
    },
    onSuccess: () => {
      invalidateFaqQueries(qc);
      setItemForm(null);
      toast.success(t("faqAdmin.save"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteItem = useMutation({
    mutationFn: (id: string) =>
      faqApi(`/api/faq/items/${id}`, { method: "DELETE" }, t("faqAdmin.deleteItemError")),
    onSuccess: () => {
      invalidateFaqQueries(qc);
      setItemForm(null);
      toast.success(t("faqAdmin.deleted"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const moveItem = useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: "up" | "down" }) =>
      faqApi(
        `/api/faq/items/${id}`,
        { method: "PATCH", body: JSON.stringify({ direction }) },
        t("faqAdmin.reorderError"),
      ),
    onSuccess: () => invalidateFaqQueries(qc),
    onError: (e: Error) => toast.error(e.message),
  });

  const itemIndex = itemForm?.id ? items.findIndex((i) => i.id === itemForm.id) : -1;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex shrink-0 flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={() => setCategoryForm({ icon: DEFAULT_FAQ_ICON, placements: ["home"], isActive: true })}>
          <FolderPlus className="size-4" />
          {t("faqAdmin.newCategory")}
        </Button>
        <Button disabled={!activeCategoryId} onClick={() => setItemForm({ isActive: true })}>
          <Plus className="size-4" />
          {t("faqAdmin.newItem")}
        </Button>
      </div>

      {catsLoading ? (
        <div className="h-12 animate-pulse rounded-xl bg-muted/60" />
      ) : categories.length === 0 ? (
        <FaqEmptyState variant="no-categories" />
      ) : (
        <>
          <FaqCategoryTabs
            categories={categories}
            activeCategoryId={activeCategoryId}
            searchQuery={searchQuery}
            onCategoryChange={(id) => {
              setActiveCategoryId(id);
              setSearchQuery("");
            }}
            onSearchChange={setSearchQuery}
            onEditCategory={() => {
              if (activeCategory) setCategoryForm(activeCategory);
            }}
          />

          {itemsLoading ? (
            <FaqItemListSkeleton />
          ) : filteredItems.length === 0 ? (
            <FaqEmptyState variant={searchQuery ? "no-search" : "no-items"} />
          ) : (
            <div className="rounded-xl border border-border/60 bg-card shadow-sm">
              <ul className="divide-y divide-border/50">
                {filteredItems.map((item) => {
                  const index = items.findIndex((i) => i.id === item.id);
                  return (
                    <FaqItemRow
                      key={item.id}
                      item={item}
                      index={index}
                      total={items.length}
                      onEdit={() => setItemForm(item)}
                      onMoveUp={() => moveItem.mutate({ id: item.id, direction: "up" })}
                      onMoveDown={() => moveItem.mutate({ id: item.id, direction: "down" })}
                    />
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}

      {categoryForm && (
        <FaqCategoryFormDialog
          form={categoryForm}
          isSaving={saveCat.isPending}
          isDeleting={deleteCat.isPending}
          onChange={setCategoryForm}
          onSave={() => saveCat.mutate(categoryForm)}
          onDelete={categoryForm.id ? () => deleteCat.mutate(categoryForm.id!) : undefined}
          onClose={() => setCategoryForm(null)}
        />
      )}

      {itemForm && activeCategory && (
        <FaqItemFormDialog
          item={itemForm}
          categoryLabel={`${activeCategory.nameBs} / ${activeCategory.nameEn}`}
          isSaving={saveItem.isPending}
          isDeleting={deleteItem.isPending}
          canMoveUp={itemIndex > 0}
          canMoveDown={itemIndex >= 0 && itemIndex < items.length - 1}
          onChange={setItemForm}
          onSave={() => saveItem.mutate(itemForm)}
          onDelete={itemForm.id ? () => deleteItem.mutate(itemForm.id!) : undefined}
          onMove={(direction) => {
            if (itemForm.id) moveItem.mutate({ id: itemForm.id, direction });
          }}
          onClose={() => setItemForm(null)}
        />
      )}
    </div>
  );
}
